/**
 * Constantes métier de la brique logistique, SANS aucune dépendance runtime (ni vine, ni Lucid) :
 * ce module est exporté par le barrel (`index.ts`), donc embarqué par Metro dans le bundle Expo.
 * Toute règle de validation (qui, elle, importe vine) reste dans `validation/logistics.ts`, en
 * deep-import uniquement.
 *
 * Point de vérité unique des énumérations du module : les modèles Lucid, les schémas Vine, le
 * contrat du solveur et le back-office s'y réfèrent plutôt que de recopier les littéraux.
 */

/** `logistics_vehicles.vehicle_type`. */
export const LOGISTICS_VEHICLE_TYPES = ['VL', 'PL', 'SL'] as const
export type LogisticsVehicleType = (typeof LOGISTICS_VEHICLE_TYPES)[number]

/**
 * Types admis en approche (atelier → hub), par ordre de préférence du solveur : le hub doit en
 * accepter au moins un en entrée (`hub_type_not_allowed` sinon).
 */
export const LOGISTICS_APPROACH_VEHICLE_TYPES = [
  'SL',
  'PL',
] as const satisfies readonly LogisticsVehicleType[]

/** Objet indexé par type de véhicule, chaque valeur calculée pour son type (cf. `mapWeekDays`). */
export const mapVehicleTypes = <T>(
  valueOf: (type: LogisticsVehicleType) => T,
): Record<LogisticsVehicleType, T> =>
  Object.fromEntries(LOGISTICS_VEHICLE_TYPES.map((type) => [type, valueOf(type)])) as Record<
    LogisticsVehicleType,
    T
  >

/**
 * Normalisation d'une plaque (brief : « plate (normalisée, unique) ») : majuscules, séparateurs
 * (espaces, tirets, points) supprimés. `AB-123-CD`, `ab 123 cd` et `AB.123.CD` normalisent tous
 * en `AB123CD`. Seule règle d'une plaque valide, partagée par l'écriture (`LogisticsVehicle`),
 * la validation de l'API et la fiche du BO : une plaque qui normalise en chaîne vide est refusée.
 */
export const normalizeLogisticsPlate = (plate: string): string =>
  plate.toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * Gabarit routier d'un camion (`logistics_vehicles`) : clés, dans l'ordre de la signature du cache
 * des trajets. Seule liste, reprise par le routage, la validation, l'effacement au changement de
 * type et la fiche du BO.
 */
export const LOGISTICS_ROAD_PARAMETER_KEYS = [
  'totalPermittedWeightKg',
  'heightCm',
  'lengthCm',
  'widthCm',
  'axleCount',
] as const

/**
 * Mise en forme d'un nom interne de camion, quelle que soit la couche (écriture, API, fiche) :
 * Unicode NFC (l'index `LOWER(internal_name)` ne confondrait pas un « é » composé et décomposé),
 * espaces rognés et ramenés à un seul (« PL  01 » et « PL 01 » sont le même camion).
 */
export const normalizeLogisticsInternalName = (name: string): string =>
  name.normalize('NFC').trim().replace(/\s+/g, ' ')

/**
 * Libellés d'affichage des types, communs à tout le module : `SL` (tracteur et semi-remorque) se
 * dit « SPL » (Super Poids Lourd, le permis qui le conduit) dans le métier et les maquettes ; le
 * code stocké reste `SL`.
 */
export const LOGISTICS_VEHICLE_TYPE_LABELS: Record<
  LogisticsVehicleType,
  { short: string; long: string }
> = {
  VL: { short: 'VL', long: 'Véhicule léger (VL)' },
  PL: { short: 'PL', long: 'Poids lourd (PL)' },
  SL: { short: 'SPL', long: 'Super Poids Lourd (SPL)' },
}

/**
 * `store_logistics_profiles.allowed_vehicle_types` : sous-ensemble strict des types de véhicule.
 * `SL` (semi-remorque, « SPL ») n'en fait jamais partie — aucune boutique ne se livre en semi.
 */
export const LOGISTICS_STORE_VEHICLE_TYPES = ['VL', 'PL'] as const
export type LogisticsStoreVehicleType = (typeof LOGISTICS_STORE_VEHICLE_TYPES)[number]

/** `logistics_driver_licences.category` : permis B/C/CE. */
export const LOGISTICS_LICENCE_CATEGORIES = ['B', 'C', 'CE'] as const
export type LogisticsLicenceCategory = (typeof LOGISTICS_LICENCE_CATEGORIES)[number]

/**
 * Permis qui conduit chaque gabarit : B → VL, C → PL, CE → SL. Seule source de cette
 * correspondance, lue par le solveur (`canDrive`) et par les pièces exigées du poids lourd.
 */
export const LOGISTICS_LICENCE_BY_VEHICLE_TYPE: Record<
  LogisticsVehicleType,
  LogisticsLicenceCategory
> = {
  VL: 'B',
  PL: 'C',
  SL: 'CE',
}

/**
 * `logistics_driver_licences.kind` : nature d'une pièce de chauffeur (#899). La table garde le nom
 * qu'elle avait quand elle ne portait que des permis ; seul un permis (`licence`) a une catégorie.
 */
export const LOGISTICS_DRIVER_DOCUMENT_KINDS = [
  'identity_card',
  'licence',
  'medical_certificate',
  'driver_card',
  'fco',
  'dpae',
  'other',
] as const
export type LogisticsDriverDocumentKind = (typeof LOGISTICS_DRIVER_DOCUMENT_KINDS)[number]

/**
 * Pièce qu'un chauffeur doit fournir : un permis nomme sa catégorie, aucune autre pièce n'en a
 * (CHECK `logistics_driver_licences_kind_category`).
 */
export type LogisticsRequiredDriverDocument =
  | { kind: 'licence'; category: LogisticsLicenceCategory }
  | { kind: Exclude<LogisticsDriverDocumentKind, 'licence' | 'other'>; category: null }

/** Libellés des pièces, ceux de la fiche chauffeur (maquette du ticket #899). */
export const LOGISTICS_DRIVER_DOCUMENT_KIND_LABELS: Record<LogisticsDriverDocumentKind, string> = {
  identity_card: 'Carte nationale d’identité',
  licence: 'Permis',
  medical_certificate: 'Attestation médicale',
  driver_card: 'Carte conducteur',
  fco: 'FCO',
  dpae: 'DPAE',
  other: 'Autres documents',
}

/** Ce qui désigne une pièce : sa nature, et sa catégorie pour un permis (`null` sinon). */
export interface LogisticsDriverDocumentIdentity {
  kind: LogisticsDriverDocumentKind
  category: LogisticsLicenceCategory | null
}

/**
 * « Permis C » pour un permis, le libellé de sa nature pour toute autre pièce. Accepte une pièce
 * telle que l'API la rend, catégorie typée nullable : un permis sans catégorie (exclu par le CHECK)
 * se nommerait « Permis ».
 */
export const driverDocumentLabel = (document: LogisticsDriverDocumentIdentity): string =>
  document.kind === 'licence' && document.category !== null
    ? `Permis ${document.category}`
    : LOGISTICS_DRIVER_DOCUMENT_KIND_LABELS[document.kind]

/** Permis du poids lourd : ceux de tout gabarit autre que le VL. */
const HEAVY_VEHICLE_LICENCE_CATEGORIES: ReadonlySet<LogisticsLicenceCategory> = new Set(
  LOGISTICS_VEHICLE_TYPES.filter((type) => type !== 'VL').map(
    (type) => LOGISTICS_LICENCE_BY_VEHICLE_TYPE[type],
  ),
)

/** Pièces que le poids lourd ajoute, dans l'ordre de la fiche chauffeur. */
const HEAVY_VEHICLE_DOCUMENT_KINDS = ['medical_certificate', 'driver_card', 'fco'] as const

const HEAVY_VEHICLE_DOCUMENT_KIND_SET: ReadonlySet<LogisticsDriverDocumentKind> = new Set(
  HEAVY_VEHICLE_DOCUMENT_KINDS,
)

/**
 * Une pièce se dépose-t-elle avec sa date de fin de validité ? Oui pour celles du poids lourd :
 * attestation médicale, carte conducteur et FCO, toutes à échéance réglementaire, et permis C ou
 * CE, limité à cinq ans et seul relu par la simulation. Sans date, une fois vérifiées, elles
 * vaudraient pour toujours. Facultative pour un permis B (ancien modèle papier sans date), une
 * carte d'identité (validité parfois prolongée au-delà de la date imprimée) et une DPAE (sans fin).
 */
export const driverDocumentRequiresValidUntil = (
  document: LogisticsDriverDocumentIdentity,
): boolean =>
  document.kind === 'licence'
    ? document.category !== null && HEAVY_VEHICLE_LICENCE_CATEGORIES.has(document.category)
    : HEAVY_VEHICLE_DOCUMENT_KIND_SET.has(document.kind)

/**
 * Fin de validité la plus lointaine acceptée au dépôt d'une pièce qui en exige une
 * (`driverDocumentRequiresValidUntil`), en années à partir d'aujourd'hui : garde-fou contre une
 * faute de frappe (2203 pour 2031), aucune de ces pièces ne valant plus de cinq ans. La date bornée
 * (`logisticsMaxDriverDocumentValidUntil`, `logistics-dates.ts`) et le message de refus sont communs
 * à l'API et au futur formulaire de la fiche.
 */
export const LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS = 10

/** Refus d'une fin de validité au-delà de `LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS`. */
export const driverDocumentValidUntilTooFarMessage = (
  document: LogisticsDriverDocumentIdentity,
): string =>
  `La date de fin de validité de la pièce « ${driverDocumentLabel(document)} » ne peut pas dépasser ${LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS} ans à partir d’aujourd’hui.`

/**
 * Pièces obligatoires d'un chauffeur selon ses permis déclarés (#899), dans l'ordre de la fiche.
 *
 * PROVISOIRE : la liste complète est « à fournir par Lucie » (ticket Notion #899). D'ici là, la
 * règle donnée par Saad le 21/09/2026 (`docs/superpowers/journal/2026-09-21-brique-logistique-decisions-saad.md`) :
 * carte nationale d'identité, un permis par catégorie déclarée et DPAE pour tous ; attestation
 * médicale, carte conducteur et FCO seulement avec un permis C ou CE. « Autres documents » n'est
 * jamais exigé ; la photo citée ce jour-là n'est ni dans le ticket ni dans la maquette.
 */
export const requiredDriverDocuments = (
  licenceCategories: readonly LogisticsLicenceCategory[],
): LogisticsRequiredDriverDocument[] => {
  const declared = LOGISTICS_LICENCE_CATEGORIES.filter((category) =>
    licenceCategories.includes(category),
  )
  const drivesHeavyVehicles = declared.some((category) =>
    HEAVY_VEHICLE_LICENCE_CATEGORIES.has(category),
  )
  return [
    { kind: 'identity_card', category: null },
    ...declared.map((category) => ({ kind: 'licence' as const, category })),
    ...(drivesHeavyVehicles
      ? HEAVY_VEHICLE_DOCUMENT_KINDS.map((kind) => ({ kind, category: null }))
      : []),
    { kind: 'dpae', category: null },
  ]
}

/** `logistics_driver_profiles.employment_type`. */
export const LOGISTICS_EMPLOYMENT_TYPES = ['internal', 'contractor'] as const
export type LogisticsEmploymentType = (typeof LOGISTICS_EMPLOYMENT_TYPES)[number]

/** `store_logistics_profiles.delivery_mode`. */
export const LOGISTICS_DELIVERY_MODES = ['autonomous', 'assisted'] as const
export type LogisticsDeliveryMode = (typeof LOGISTICS_DELIVERY_MODES)[number]

/**
 * `logistics_simulations.kind` : type de tournée. `last_mile` = livraison directe atelier →
 * boutiques, `approach` = acheminement jusqu'à un hub de rupture de charge. `full` (les deux flux
 * combinés, ticket #847) n'est plus créé depuis #902 : acheminement et dernier km sont deux
 * tournées indépendantes (`LOGISTICS_SIMULATION_CREATE_KINDS`), mais les simulations `full` déjà
 * enregistrées restent lisibles. Le moteur ne lit pas `kind`, mais `kind` contraint `hubIds`
 * (`logisticsHubIdsForKindError`, `validation/logistics.ts`) : `last_mile` sans hub,
 * `approach`/`full` avec exactement un hub — et c'est `hubIds` qui ouvre au moteur la famille via
 * hub, qu'il compare à la livraison directe.
 */
export const LOGISTICS_SIMULATION_KINDS = ['last_mile', 'approach', 'full'] as const
export type LogisticsSimulationKind = (typeof LOGISTICS_SIMULATION_KINDS)[number]

/** Types qu'une création ou un changement de type accepte (#902) : jamais `full`. */
export const LOGISTICS_SIMULATION_CREATE_KINDS = ['last_mile', 'approach'] as const
export type LogisticsSimulationCreateKind = (typeof LOGISTICS_SIMULATION_CREATE_KINDS)[number]

/** Rattachement d'un véhicule ou d'un collaborateur logistique : un atelier OU un hub. */
export interface LogisticsHome {
  homeCentralKitchenId: number | null
  homeHubId: number | null
}

/** Rattachement à exactement un atelier OU un hub (CHECK `*_single_home` / `*_role_home` en base). */
export const hasExactlyOneHome = (home: LogisticsHome): boolean =>
  (home.homeCentralKitchenId !== null) !== (home.homeHubId !== null)

// --- Paramètres de simulation -----------------------------------------------------------

/**
 * Paramètres de simulation logistique.
 *
 * Deux familles : ceux qui ont une valeur par défaut défendable (`LOGISTICS_DEFAULT_PARAMETERS`)
 * et ceux qui n'en ont aucune (`LOGISTICS_REQUIRED_PARAMETERS`) — les seconds ne s'inventent pas,
 * une simulation lancée sans eux conclut `input_incomplete` plutôt que de chiffrer au hasard.
 */
export const LOGISTICS_DEFAULT_PARAMETERS = {
  /** Toutes les boutiques livrées avant cette heure locale. */
  deliveryDeadline: '10:30',
  /** Durée d'un arrêt boutique : 20 min. */
  stopDurationSeconds: 1200,
  /** Rupture de charge sur un hub : 45 min. À confirmer par Saad. */
  hubTransferDurationSeconds: 2700,
  /**
   * Conduite continue maximale avant pause obligatoire : 4 h 30. Confirmé par Saad le 21/09/2026,
   * qui corrigeait la maquette — « c'est quatre heures et demie avec une pause de 45 minutes,
   * encore 4 h 30 avec une pause de 45 minutes », pour 9 h de conduite au total.
   */
  maxContinuousDrivingMinutes: 270,
  /** Pause obligatoire après la conduite continue maximale : 45 min. Confirmé le 21/09/2026. */
  mandatoryBreakMinutes: 45,
  /** Vol d'oiseau → route, adaptateur de dev uniquement (quand aucune matrice mesurée). */
  detourFactor: 1.3,
} as const

export type LogisticsDefaultedParameter = keyof typeof LOGISTICS_DEFAULT_PARAMETERS

/**
 * Sans défaut : à confirmer par Saad.
 *
 * L'unité de charge est le SUPPORT, palette 800×1200 ou pali 800×600 — décision du 21/09/2026 :
 * « on a charge utile, palettes, palis, c'est ça qui décidera des commandes de demain », et « pour
 * l'instant on laisse palettes et palis, mais avec le temps on ne raisonnera qu'en palis ». La
 * cagette a disparu du vocabulaire métier, elle disparaît d'ici.
 *
 * Saad n'a donné ni le rapport palette → palis (son exemple « 33 palettes, 66 palis » le suggère
 * à 2, il ne l'a pas énoncé comme règle), ni le poids d'un support. Ces trois valeurs, comme le coût
 * carburant au kilomètre (`fuelCostPerKmCents`), restent donc requises et sans défaut : une
 * simulation lancée sans ces quatre valeurs conclut `input_incomplete`.
 */
export const LOGISTICS_REQUIRED_PARAMETERS = [
  'palisPerPallet',
  'palletWeightKg',
  'paliWeightKg',
  'fuelCostPerKmCents',
] as const

export type LogisticsRequiredParameter = (typeof LOGISTICS_REQUIRED_PARAMETERS)[number]

/**
 * Sans défaut ET jamais requis : leur absence n'est pas une donnée manquante, c'est l'absence de
 * contrainte (ticket #855, onglet Contraintes). Contrôlés par service à la fin de l'ordonnancement
 * (`route_duration_exceeded` / `route_distance_exceeded`, solveur).
 */
export const LOGISTICS_OPTIONAL_PARAMETERS = [
  'maxRouteDurationSeconds',
  'maxRouteDistanceMeters',
  'vehicleIds',
] as const

export type LogisticsOptionalParameter = (typeof LOGISTICS_OPTIONAL_PARAMETERS)[number]

/**
 * Heure `HH:MM` → secondes depuis minuit : seule conversion des heures d'une tournée, lue par le
 * moteur (`solver/common.ts`) et par le BO (frise des alternatives).
 */
export const logisticsTimeToSeconds = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60
}

/** Secondes depuis minuit → `HH:MM` (inverse de `logisticsTimeToSeconds`, au-delà de minuit : `24:30`). */
export const logisticsSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Horizon maximal de la date d'effet d'une bascule en opérationnel (#860) : la validation remplace
 * AUSSITÔT les tournées en vigueur des boutiques communes, une date lointaine (faute de frappe,
 * 2062 pour 2026) les ferait tomber aujourd'hui pour une remplaçante qui n'entrerait en vigueur que
 * dans des années. Borne commune à l'API (VineJS) et au formulaire du BO (Zod).
 */
export const LOGISTICS_MAX_EFFECTIVE_FROM_YEARS = 1
/** Fuseau des dates d'exploitation (date d'effet, « aujourd'hui ») : le même côté API et BO. */
export const LOGISTICS_BUSINESS_ZONE = 'Europe/Paris'
export const LOGISTICS_EFFECTIVE_FROM_TOO_FAR_MESSAGE = `La date de prise d’effet ne peut pas dépasser ${LOGISTICS_MAX_EFFECTIVE_FROM_YEARS} an${LOGISTICS_MAX_EFFECTIVE_FROM_YEARS > 1 ? 's' : ''} à partir d’aujourd’hui.`

/** Ce que le moteur et le BO lisent d'un véhicule pour juger s'il peut servir une tournée. */
export interface LogisticsVehicleEligibility {
  isActive: boolean
  pallets800x1200: number | null
  palis800x600: number | null
  homeCentralKitchenId: number | null
  homeHubId: number | null
}

/** Au moins un contenant déclaré (palettes ou palis) : sans quoi le véhicule ne transporte rien. */
export const logisticsVehicleHasCapacity = (vehicle: LogisticsVehicleEligibility): boolean =>
  (vehicle.pallets800x1200 ?? 0) > 0 || (vehicle.palis800x600 ?? 0) > 0

/**
 * Capacité en EMPLACEMENTS DE PALI, l'unité commune du calcul : un véhicule déclare la même surface
 * en palettes et en palis, on garde le maximum des deux expressions converties (ce qui couvre aussi
 * un véhicule qui n'en déclare qu'une). `palisPerPallet` absent contribue pour zéro. Seule
 * définition, lue par le moteur (`resolveVehicles`) et par le détail d'une tournée.
 */
export const logisticsPaliSlotCapacity = (
  vehicle: Pick<LogisticsVehicleEligibility, 'pallets800x1200' | 'palis800x600'>,
  palisPerPallet: number | undefined,
): number =>
  Math.max((vehicle.pallets800x1200 ?? 0) * (palisPerPallet ?? 0), vehicle.palis800x600 ?? 0)

/**
 * Rattaché à l'atelier de la tournée ou à l'un de ses hubs. Côté base, le même périmètre est la
 * scope `LogisticsVehicle.inPerimeter` (moteur et contrôle à l'écriture).
 */
export const logisticsVehicleInPerimeter = (
  vehicle: LogisticsVehicleEligibility,
  centralKitchenId: number,
  hubIds: readonly number[],
): boolean =>
  vehicle.homeCentralKitchenId === centralKitchenId ||
  (vehicle.homeHubId !== null && hubIds.includes(vehicle.homeHubId))

/**
 * Camion que le moteur peut mobiliser pour une tournée : actif, avec un contenant, rattaché à
 * l'atelier ou à l'un de ses hubs. Seule définition côté BO (création, onglet Contraintes) ; côté
 * base, la même règle est portée par `resolveVehicles`.
 */
export const logisticsVehicleMobilizable = (
  vehicle: LogisticsVehicleEligibility,
  centralKitchenId: number,
  hubIds: readonly number[],
): boolean =>
  vehicle.isActive &&
  logisticsVehicleHasCapacity(vehicle) &&
  logisticsVehicleInPerimeter(vehicle, centralKitchenId, hubIds)

/**
 * Jour d'arrivée d'une tournée (#903), par rapport à son jour de départ : le jour même (0) ou le
 * lendemain (1), pour une tournée partie le soir. Les horaires sont les mêmes tous les jours
 * d'activité : un seul décalage vaut pour chaque départ.
 */
export const LOGISTICS_ARRIVAL_DAY_OFFSETS = [0, 1] as const
export type LogisticsArrivalDayOffset = (typeof LOGISTICS_ARRIVAL_DAY_OFFSETS)[number]

/** Coût carburant au kilomètre, par type de véhicule (centimes). */
export type LogisticsFuelCostPerKmCents = Partial<Record<LogisticsVehicleType, number>>

/** Paramètres tels qu'ils sortent de la résolution : défauts garantis, requis encore optionnels. */
export interface LogisticsParameters {
  deliveryDeadline: string
  stopDurationSeconds: number
  hubTransferDurationSeconds: number
  maxContinuousDrivingMinutes: number
  mandatoryBreakMinutes: number
  detourFactor: number
  /** Emplacements de pali occupés par une palette : l'unité commune du calcul de capacité. */
  palisPerPallet?: number
  palletWeightKg?: number
  paliWeightKg?: number
  fuelCostPerKmCents?: LogisticsFuelCostPerKmCents
  /** Temps de trajet maximal d'un service (secondes), sans défaut : pas de contrainte si absent. */
  maxRouteDurationSeconds?: number
  /** Kilométrage maximal d'un service (mètres), sans défaut : pas de contrainte si absent. */
  maxRouteDistanceMeters?: number
  /**
   * Camion de la tournée (`logistics_vehicles.id`) : un seul à l'écriture, requis à la création
   * (#901, `logisticsVehicleIdsError`, appliquée par les contrôleurs et non par `parametersField`). Les simulations antérieures se relisent telles quelles : plusieurs
   * camions (« Camions mobilisables », ticket #855), ou aucun = Automatique, toute la flotte de
   * l'atelier et des hubs reste candidate. Un id présent ici mais non mobilisable (inactif, autre
   * rattachement) reste exclu par `resolveVehicles` : ce paramètre RESTREINT la flotte candidate,
   * il ne l'étend jamais.
   */
  vehicleIds?: number[]
}

/** Surcharges stockées sur `logistics_simulations.parameters` : tout est optionnel. */
export type LogisticsParametersOverrides = Partial<LogisticsParameters>
